import { supabase } from './supabase';

export const WORKER_URL = 'https://mycarcosts.gtreulands.workers.dev';

export interface UploadResult {
  imageUrl: string;
  thumbUrl?: string;
  status: string;
  sizeBytes?: number;
}

export class UploadError extends Error {
  code?: string;
  limit?: number;

  constructor(message: string, code?: string, limit?: number) {
    super(message);
    this.name = 'UploadError';
    this.code = code;
    this.limit = limit;
  }
}

export async function uploadImage(
  file: File,
  folder: string = 'general',
  type?: 'car' | 'expense' | 'part' | 'car_image' | 'expense_image' | 'note',
  id?: string,
  thumb?: Blob | File,
  sessionId?: string,
  carId?: string
): Promise<UploadResult> {
  const { data: { session } } = await supabase.auth.getSession();
  const isAnonymousAudit = folder === 'audit';

  if (!session && !isAnonymousAudit) {
    throw new Error('User not authenticated');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);
  if (type) formData.append('type', type);
  if (id) formData.append('id', id);
  if (thumb) formData.append('thumb', thumb, 'thumb.jpg');
  if (sessionId) formData.append('sessionId', sessionId);
  if (carId) formData.append('carId', carId);

  const response = await fetch(`${WORKER_URL}/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session?.access_token || ''}`,
    },
    body: formData,
  });

  let result;
  try {
    result = await response.json();
    if (result && result.logs) {
      console.log('Worker Debug Logs:', result.logs);
    }
  } catch (e) {
    console.error('Failed to parse worker response as JSON');
  }

  if (!response.ok) {
    if (result && result.code) {
      throw new UploadError(result.error || 'Upload failed', result.code, result.limit);
    }
    throw new Error(result?.error || 'Failed to upload image');
  }

  return result;
}

export async function deleteImage(imageUrl: string, thumbUrl?: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('User not authenticated');
  }

  const response = await fetch(`${WORKER_URL}/delete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ imageUrl, thumbUrl }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete image');
  }
}

// Delete temp files by their URLs - used when user cancels form or removes unsaved image
export async function deleteTempFiles(fileUrls: string[]): Promise<void> {
  if (!fileUrls || fileUrls.length === 0) return;

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return; // Not authenticated, cron job will clean up later

  try {
    await fetch(`${WORKER_URL}/delete-temp`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileUrls }),
    });
    // Fire-and-forget: we don't throw on error, cron job is the safety net
  } catch (e) {
    console.warn('deleteTempFiles failed (cron will clean up):', e);
  }
}

export async function submitContactForm(title: string, message: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (session) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`${WORKER_URL}/contact`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ title, message }),
  });

  if (!response.ok) {
    let errorMessage = 'Failed to submit contact form';
    try {
      const error = await response.json();
      errorMessage = error.error || errorMessage;
    } catch (e) {
      // Not JSON
    }
    throw new Error(errorMessage);
  }
}

export async function triggerAudit(auditId: string): Promise<void> {
  const response = await fetch(`${WORKER_URL}/audit/trigger`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ auditId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Trigger failed: ${errorText}`);
  }
}
